import { useApi } from '../../hooks/useApi';
import { ChatResponse, ChatHistoryResponse, TokenInit } from '../../types/chat';
import { ORGANIZATION_ID_PATTERN } from '../../constants/patterns';

export interface StreamChunk {
  type: 'start' | 'chunk' | 'tool' | 'end' | 'error' | 'suggestions' | 'custom';
  content: Array<{ type: string; text: string; index: number; trace_id?: string }> | string;
  metadata: {
    trace_id?: string;
    generated_html?: string;
    error_code?: string;
    error_message?: string;
    checkpoint_id?: string;
    suggestions?: string[];
    progress_estimate?: number;
    waiting_sentences?: string[];
  };
}

interface ErrorResponse {
  error?: string;
  code?: string;
}

export const useChatApi = () => {
  const { fetchWithSession } = useApi();

  const sendMessage = async (message: string): Promise<ChatResponse> => {
    const response = await fetchWithSession<ChatResponse>('/api/v1/chat/chat', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });

    if (response instanceof Response) {
      throw new Error('Unexpected streaming response');
    }

    if (response.error || !response.data) {
      throw new Error(response.error || 'Failed to send message');
    }

    return response.data;
  };

  const sendStreamingMessage = async (
    message: string,
    onChunk: (chunk: string, checkpoint_id?: string, trace_id?: string) => void,
    onComplete: (html?: string, trace_id?: string, error?: string, checkpoint_id?: string) => void,
    checkpoint_id?: string,
    onSuggestions?: (suggestions: string[]) => void,
    initToken?: TokenInit,
    onProgress?: (
      waitingSentences: string[] | undefined,
      progressEstimate: number | undefined,
      traceId?: string,
      checkpointId?: string
    ) => void
  ): Promise<void> => {
    try {
      // Get current organization ID from URL at the time of request
      const currentPathname = window.location.pathname;
      const pathMatch = currentPathname.match(ORGANIZATION_ID_PATTERN);
      // Fall back to user's primary organization if not in URL (e.g., standalone/local mode)
      const primaryOrgId = initToken?.user_data?.organization?.id?.toString() || null;
      const currentOrgId = pathMatch ? pathMatch[1] : primaryOrgId;

      const response = await fetchWithSession(`/api/v1/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          checkpoint_id: checkpoint_id || '',
          token: initToken || null,
          current_organization_id: currentOrgId
        }),
      }, true);
      console.log('Response:', response);

      // Handle non-streaming response (error situations)
      if (!(response instanceof Response)) {
        console.log('Response:', response);
        const errorResponse = response as ErrorResponse;
        
        // Check for token expiration in the correct format
        if (
          errorResponse.code === 'TOKEN_EXPIRED' 
        ) {
          // Log the expiration event for debugging
          console.log('Token expiration detected, showing alert');
          
          // Show token expiration modal/notification
          showTokenExpirationAlert();
          onComplete(undefined, undefined, "Your session has expired. Please refresh the page to continue.", checkpoint_id);
          return;
        }
        
        // Check for trial/license expiration
        if (
          errorResponse.code === 'trial_expired' ||
          errorResponse.code === 'license_expired' ||
          errorResponse.code === 'no_license'
        ) {
          
          // Dispatch event to update trial state in the UI
          const event = new CustomEvent('trial-status-changed', {
            detail: { 
              code: errorResponse.code,
              message: errorResponse.error 
            }
          });
          window.dispatchEvent(event);
          
          // Show the user-friendly error message
          onComplete(undefined, undefined, errorResponse.error, checkpoint_id);
          return;
        }
        
        // Extract detailed error message instead of throwing generic error
        const errorMessage = errorResponse.error || 
                            (errorResponse.code ? `Error code: ${errorResponse.code}` : 'Unknown API error');
        console.error('[Debug] Stream request failed:', errorMessage, errorResponse);
        onComplete(undefined, undefined, errorMessage, checkpoint_id);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        const error = 'No reader available for stream';
        console.error('[Debug]', error);
        onComplete(undefined, undefined, error, checkpoint_id);
        return;
      }

      const decoder = new TextDecoder();
      let isInAnswer = false;
      let lastCheckpointId = checkpoint_id;

      while (true) {
        try {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ')) continue;

            const jsonStr = line.replace('data: ', '');
            try {
              const data: StreamChunk = JSON.parse(jsonStr);

              if (data.type === 'start') {
                console.log('[Debug] Stream started');
                continue;
              }

              if (data.type === 'end') {
                onComplete(data.metadata.generated_html, data.metadata.trace_id);
                continue;
              }

              if (data.type === 'error') {
                console.error('[Debug] Stream error:', data.metadata.error_message);
                onComplete(undefined, data.metadata.trace_id, data.metadata.error_message, lastCheckpointId);
                return;
              }

              // NEW: Handle custom (progress) chunks
              if (data.type === 'custom') {
                // For your example, data.content === "progress"
                if (data.content === 'progress' && onProgress) {
                  onProgress(
                    data.metadata.waiting_sentences,
                    data.metadata.progress_estimate,
                    data.metadata.trace_id,
                    data.metadata.checkpoint_id
                  );
                }
                continue;
              }

              if (data.type === 'chunk' || data.type === 'tool') {
                
                // Hide the progress UI once the next real chunk arrives
                if (onProgress) {
                  onProgress(undefined, undefined, data.metadata?.trace_id);
                }

                let content: any = 'null';
                if (Array.isArray(data.content)) {
                  content = data.content[0];
                } else {
                  content = data.content;
                }

                if (!content) continue;

                let text = content.text || '';
                
                // Handle answer tags
                if (text.includes('<answer')) {
                  isInAnswer = true;
                  text = text.replace(/<answer>/g, '').replace(/<\/answer>/g, '');
                  text = text.replace('<answer>', '').replace(/<\/answer>/g, '');
                  text = text.replace('<answer', '').replace(/<\/answer>/g, '');
                }

                if (text.includes('</answer')) {
                  isInAnswer = false;
                  text = text.replace('</answer>', '').replace(/<\/answer>/g, '');
                  text = text.replace('</answer', '').replace(/<\/answer>/g, '');
                }

                if (text.includes('</thinking')) {
                  text = text.replace('</thinking>', '').replace(/<\/thinking>/g, '');
                  text = text.replace('</thinking', '').replace(/<\/thinking>/g, '');
                }

                if (text.includes('<thinking')) {
                  text = text.replace('<thinking>', '').replace(/<\/thinking>/g, '');
                  text = text.replace('<thinking', '').replace(/<\/thinking>/g, '');
                }

                if(text === ">") {
                  continue;
                }

                if(text.startsWith(">")) {
                  text = text.replace('>', '');
                }

                // Update lastCheckpointId if new one is provided
                if (data.metadata.checkpoint_id) {
                  lastCheckpointId = data.metadata.checkpoint_id;
                }
                onChunk(text, lastCheckpointId, data.metadata?.trace_id);
              }
              
              if (data.type === 'suggestions') {
                if (onSuggestions) {
                  onSuggestions(data.metadata.suggestions || []);
                } else {
                  console.log("[Debug] onSuggestions callback not provided");
                }
                return; // Since this will be the last thing
              }
            } catch (e) {
              console.error('[Debug] Error parsing chunk:', {
                error: e,
                rawLine: line,
                jsonStr: jsonStr,
                isInAnswer: isInAnswer
              });
              // Don't throw here, try to continue processing other chunks
              continue;
            }
          }
        } catch (streamError: unknown) {
          console.error('[Debug] Error reading stream:', streamError);
          onComplete(undefined, undefined, 
            streamError instanceof Error ? streamError.message : 'Stream processing error',
            lastCheckpointId
          );
          return;
        }
      }

      console.log('[Debug] Stream completed without explicit end marker');
      onComplete(undefined, undefined, undefined, lastCheckpointId);
    } catch (error) {
      // Check if error is a response object with a token expiration
      if (error instanceof Error && error.message === 'TOKEN_EXPIRED') {
        showTokenExpirationAlert();
        onComplete(undefined, undefined, "Your session has expired. Please refresh the page to continue.", checkpoint_id);
        return;
      }

      // Handle other errors
      console.error('[Debug] Error sending message:', error);
      onComplete(undefined, undefined, `Error: ${error instanceof Error ? error.message : 'Unknown error'}`, checkpoint_id);
    }
  };

  const getChatHistory = async (sessionId: string): Promise<ChatHistoryResponse> => {
    const response = await fetchWithSession<ChatHistoryResponse>(`/api/v1/chat/history/${sessionId}`);

    if (response instanceof Response) {
      throw new Error('Unexpected streaming response');
    }

    if (response.error || !response.data) {
      throw new Error(response.error || 'Failed to fetch chat history');
    }

    return response.data;
  };

  // Create a function to show the token expiration alert
  const showTokenExpirationAlert = () => {
    // Dispatch event to show notification/modal
    const event = new CustomEvent('session-expired');
    window.dispatchEvent(event);
  };

  return {
    sendMessage,
    sendStreamingMessage,
    getChatHistory,
  };
};